import { Color, Spacing } from '@persona-id/design-tokens';
import { ArrowLeftIcon, LoaderIcon } from '@persona-id/design-tokens/icons';
import { groupBy, range } from 'lodash/fp';
import { Fragment, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom-v5-compat';
import styled, { ThemeProvider } from 'styled-components';

import FeatureFlagged from 'dashboard/components/FeatureFlagged';
import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import NullState from 'dashboard/components-lib/NullState';
import { DEFAULT_DOCUMENT_GENERIC_CONFIG, DocumentGeneric, Line } from 'dashboard/models/document';
import {
  removeFocusedExtraction,
  removeHoveredExtraction,
  resetDocumentGenericState,
  setExtractionFilters,
  setExtractions,
  setFileNumPages,
  setFocusedExtraction,
  setHoveredExtraction,
  setInSearchMode,
  setSearchHighlights,
  setSearchIndex,
  setTranslationLanguage,
  setTranslationLoading,
  setTranslations,
} from 'dashboard/redux/actions/document-viewer-document-generic';
import { showMessage } from 'dashboard/redux/actions/messages';
import { DocumentViewerSingletonExtraction } from 'dashboard/redux/reducers/document-viewer-document-generic';
import { RootState } from 'dashboard/redux/store';
import { Z_INDICES } from 'dashboard/styles/variables';
import { DashboardDocumentViewerViewProps } from 'dashboard/views/DashboardDocumentViewerView/DashboardDocumentViewerView';
import DocumentTranslationText from 'dashboard/views/DashboardDocumentViewerView/DocumentTranslationText';
import DocumentViewerDownload from 'dashboard/views/DashboardDocumentViewerView/DocumentViewerDownload';
import DocumentViewerExtractionFilter from 'dashboard/views/DashboardDocumentViewerView/DocumentViewerExtractionFilter';
import DocumentViewerHeader from 'dashboard/views/DashboardDocumentViewerView/DocumentViewerHeader';
import DocumentViewerRenderer from 'dashboard/views/DashboardDocumentViewerView/DocumentViewerRenderer';
import DocumentViewerRotate from 'dashboard/views/DashboardDocumentViewerView/DocumentViewerRotate';
import DocumentViewerSearch, {
  DocumentSearchMatch,
} from 'dashboard/views/DashboardDocumentViewerView/DocumentViewerSearch';
import DocumentViewerTranslateDropdown from 'dashboard/views/DashboardDocumentViewerView/DocumentViewerTranslateDropdown';
import DocumentViewerZoom from 'dashboard/views/DashboardDocumentViewerView/DocumentViewerZoom';
import ExtractionHighlights from 'dashboard/views/DashboardDocumentViewerView/ExtractionHighlights';
import ExtractionSidebar from 'dashboard/views/DashboardDocumentViewerView/ExtractionSidebar';
import SearchHighlights from 'dashboard/views/DashboardDocumentViewerView/SearchHighlights';
import Button from 'design-system/components/Button';
import { Label } from 'design-system/components/Text';
import ToggleSlider from 'design-system/components/ToggleSlider';
import { paragraphMixins } from 'design-system/mixins/text';
import { darkTheme } from 'design-system/theme';
import { rotate } from 'design-system/variables/keyframes';
import EventsClient from 'lib/clients/EventsClient';
import { captureMessage } from 'lib/reporting';

interface PageAnnotation {
  extractions: DocumentViewerSingletonExtraction[];
  searchMatches: DocumentSearchMatch[];
  translatedTexts: Line[];
}

interface FileAnnotation {
  pageAnnotations: PageAnnotation[];
}

type DocumentViewerBaseProps = Omit<
  DashboardDocumentViewerViewProps,
  // document is set as DocumentGeneric in DocumentGenericViewerStateProps
  'resetBaseState' | 'document'
>;

export type GenericDocumentViewerProps = DocumentViewerBaseProps &
  PropsFromRedux & { document: DocumentGeneric };

const DocumentViewerWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
  background: ${({ theme }) => theme.color.background.primary};
  color-scheme: dark;
`;

const HeaderLeftItems = styled('div')`
  display: flex;
  align-items: center;
  width: 15%;
`;
const HeaderCenterItems = styled('div')`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Divider = styled('div')`
  margin: 0px ${Spacing[1]};
  border-left: 1px solid ${Color.CoreSlate900};
  height: 36px;
`;

const DocumentContent = styled('div')`
  display: flex;
  min-height: 0;
  height: 100%;
  gap: 40px;
  padding: 0 40px 0;
  @media only screen and (max-width: 900px) {
    overflow-x: auto;
  }
`;

const FilenameLabel = styled('span')`
  ${paragraphMixins.lgBold}
  width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ToggleLabel = styled('div')`
  margin: 0 8px;
`;

interface DocumentViewerRendererContainerProps {
  translationLoading?: boolean;
}

const DocumentViewerRendererContainer = styled('div')<DocumentViewerRendererContainerProps>`
  position: relative;
  flex: 5 1 70%;
  overflow: hidden;
`;

const LoadingContainer = styled.div`
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  z-index: ${Z_INDICES.loadingOverlays};
  // last two digits in hex code specifies alpha
  // 80 is 0.5 alpha
  background-color: ${({ theme }) => theme.color.background.primary + '80'};
`;

const LoaderSpinnerAndLabelContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${Spacing[2]};
  background-color: ${Color.PrimaryWhite};
  border-radius: ${({ theme }) => theme.card.borderRadius};
`;

const LoaderSpinner = styled(LoaderIcon)`
  height: 22px;
  width: 22px;
  color: ${({ theme }) => theme.color.primary.brand700};
  animation: ${rotate} linear 1s infinite;
`;

const LoaderLabel = styled(Label).attrs(() => ({ size: 'lg', weight: 'bold' }))`
  margin-left: ${Spacing[2]};
  color: ${Color.PrimaryBlack};
  font-weight: 400;
  border-radius: ${({ theme }) => theme.card.borderRadius};
`;

interface LoadingModalProps {
  title?: string;
}

const LoadingLabel = ({ title = 'Loading...' }: LoadingModalProps) => {
  return (
    <LoadingContainer>
      <LoaderSpinnerAndLabelContainer>
        <LoaderSpinner aria-label='Loading Spinner' />
        <LoaderLabel>{title}</LoaderLabel>
      </LoaderSpinnerAndLabelContainer>
    </LoadingContainer>
  );
};

const DocumentGenericViewer = ({
  document,
  fileLoading,
  resetDocumentGenericState,
  extractionFilters,
  focusedExtractions,
  setFocusedExtraction,
  removeFocusedExtraction,
  hoveredExtractions,
  setHoveredExtraction,
  removeHoveredExtraction,
  rotateDocument,
  setExtractionFilters,
  rotation,
  zoomScale,
  nullStateMessage,
  nullStateRedirect,
  setZoomScale,
  incrementZoomScale,
  decrementZoomScale,
  zoomVisualValue,
  setZoomVisualValue,
  extractions,
  setExtractions,
  inSearchMode,
  searchMatches,
  searchIndex,
  setInSearchMode,
  setSearchHighlights,
  setSearchIndex,
  setFileNumPages,
  numPagesPerFile,
  translationLoading,
  setTranslationLoading,
  translations,
  setTranslations,
  translationLanguage,
  setTranslationLanguage,
}: GenericDocumentViewerProps) => {
  useEffect(() => {
    // clean up state when component is unmounting
    return () => {
      resetDocumentGenericState();
    };
  }, []);

  const { files, 'files-normalized': filesNormalized } = document.attributes;
  const pages = useMemo(() => document.attributes.pages || [], [document]);
  const extractionResponses = useMemo(
    () => document.attributes['extraction-responses'] || [],
    [document],
  );
  const { extractions: extractionConfigs } = useMemo(
    () => document.attributes.config || DEFAULT_DOCUMENT_GENERIC_CONFIG,
    [document],
  );

  // if the document has not been processed it does not have normalized files
  // we default to using normalized files since some image formats that are not compatible
  // with browsers (HEIC images) have been converted to images
  const displayFiles = files.length === filesNormalized.length ? filesNormalized : files;
  const documentFileContainerRef = useRef(null);
  const [showTranslation, setShowTranslation] = useState(true);

  useEffect(() => {
    setShowTranslation(true);
  }, [translationLanguage]);

  const navigate = useNavigate();
  const location = useLocation();

  const fileAnnotations: FileAnnotation[] = useMemo(() => {
    try {
      const notFilteredExtractions = extractions.filter(
        (extraction) => !extraction.extractionGroup.filtered,
      );
      const extractionsByPage = groupBy(
        (x) => x.result.page,
        notFilteredExtractions.flatMap((extraction) =>
          extraction.results.map((result) => ({
            extractionGroup: extraction.extractionGroup,
            result: result,
          })),
        ),
      );
      let currentPage = 1;
      return numPagesPerFile.map((numPages) => {
        numPages = numPages || 1;
        const pageAnnotation = {
          pageAnnotations: range(currentPage, currentPage + numPages).map((pageNum) => ({
            searchMatches: searchMatches.filter((match) => match.pageNumber === pageNum),
            extractions: extractionsByPage[pageNum] || [],
            translatedTexts:
              translations && translationLanguage && pages.length >= pageNum
                ? pages[pageNum - 1].lines.map((line) => ({
                    text: translations[line.text]?.[translationLanguage] || '',
                    vertices: line.vertices,
                  }))
                : [],
          })),
        };
        currentPage = currentPage + numPages;
        return pageAnnotation;
      });
    } catch (error) {
      captureMessage('Error when rendering fileAnnotations', 'error', {
        error,
        component: 'DocumentGenericViewer',
      });
      return [];
    }
  }, [extractions, numPagesPerFile, pages, searchMatches, translationLanguage, translations]);

  useEffect(() => {
    if (document) {
      EventsClient.logAmplitude('document.document-viewer.document-generic.view', {
        documentToken: document.id,
      });
      setExtractions(extractionResponses, extractionConfigs);
    }
  }, [document, extractionConfigs, extractionResponses, setExtractions]);

  const inquiryId = document?.relationships.inquiry?.data?.id;

  if (fileLoading) {
    return <LoadingIndicator fullscreen />;
  } else if (files.length === 0) {
    return (
      <NullState
        message={
          document.attributes['redacted-at']
            ? 'The document you are accessing has been redacted and is no longer available. This may have been done manually, or as a result of a redaction policy set by your administrator.'
            : 'We cannot find the document you are looking for. It is possible that the document is still processing or there was an error during upload. Please try refreshing the page to access the document again.'
        }
        backRoute={nullStateRedirect}
      />
    );
  }

  let fileAnnotationsOverlay: ReactNode[][] | undefined = undefined;
  try {
    fileAnnotationsOverlay = fileAnnotations.map((fileAnnotations) =>
      fileAnnotations.pageAnnotations.map(
        ({ extractions, searchMatches, translatedTexts }, pageIndex) =>
          showTranslation && translatedTexts.length > 0 ? (
            translatedTexts.map((translatedLine, lineIndex) => {
              return (
                <DocumentTranslationText
                  key={`document-viewer__translation-highlight-${pageIndex}-${lineIndex}}`}
                  text={translatedLine.text}
                  vertices={translatedLine.vertices}
                  zoomScale={zoomScale}
                />
              );
            })
          ) : (
            <Fragment key={pageIndex}>
              {extractions.length > 0 && (
                <ExtractionHighlights
                  extractions={extractions || []}
                  focusedExtractions={focusedExtractions}
                  setFocusedExtraction={setFocusedExtraction}
                  removeFocusedExtraction={removeFocusedExtraction}
                  hoveredExtractions={hoveredExtractions}
                  setHoveredExtraction={setHoveredExtraction}
                  removeHoveredExtraction={removeHoveredExtraction}
                  hideFill={inSearchMode}
                  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                  documentFileContainerRef={documentFileContainerRef}
                />
              )}
              {inSearchMode && searchMatches.length > 0 && (
                <SearchHighlights searchMatches={searchMatches} focusedIndex={searchIndex} />
              )}
            </Fragment>
          ),
      ),
    );
  } catch (error) {
    captureMessage('Error when rendering fileAnnotationsOverlay', 'error', {
      error,
      component: 'DocumentGenericViewer',
    });
  }

  return (
    <ThemeProvider theme={darkTheme}>
      <DocumentViewerWrapper id='document-viewer' data-test='document-viewer'>
        <DocumentViewerHeader data-test='document-viewer__header'>
          <>
            <HeaderLeftItems>
              <Button
                data-test='document-viewer__header--back-button'
                id='document-viewer__header--back-button'
                onClick={() => {
                  // allow the user to go back if were redirected internally for best UX
                  if (location.state?.internalRedirect) {
                    navigate(-1);
                  } else {
                    navigate(
                      inquiryId ? `/dashboard/inquiries/${inquiryId}` : '/dashboard/inquiries',
                    );
                  }
                }}
                width='auto'
                variant='primary'
                prefixIcon={<ArrowLeftIcon />}
                aria-label='Go back'
              />
              <FilenameLabel>
                {displayFiles?.[0].filename}
                {displayFiles.length > 1 &&
                  ` and ${displayFiles.length - 1} other ${
                    displayFiles.length - 1 === 1 ? 'file' : 'files'
                  }`}
              </FilenameLabel>
            </HeaderLeftItems>
            <HeaderCenterItems>
              <DocumentViewerZoom
                setZoomScale={setZoomScale}
                zoomScale={zoomScale}
                incrementZoom={incrementZoomScale}
                decrementZoom={decrementZoomScale}
                zoomVisualValue={zoomVisualValue}
                setZoomVisualValue={setZoomVisualValue}
              />
              <DocumentViewerRotate rotateDocument={rotateDocument} />
              <Divider />
              <DocumentViewerSearch
                document={document}
                searchMatches={searchMatches}
                setSearchHighlights={setSearchHighlights}
                currentSearchIndex={searchIndex}
                setCurrentSearchIndex={setSearchIndex}
                setInSearchMode={setInSearchMode}
                inSearchMode={inSearchMode}
                disabled={!!translationLanguage && showTranslation}
                disabledTooltipText='Search is disabled when the document is translated'
              />
              {extractions.length > 0 && (
                <DocumentViewerExtractionFilter
                  extractionFilters={extractionFilters}
                  setExtractionFilters={setExtractionFilters}
                />
              )}
              {pages.length > 0 && (
                <FeatureFlagged featureFlag='dashboard:enable-translation-for-document-viewer'>
                  <Divider />
                  <DocumentViewerTranslateDropdown
                    document={document}
                    setTranslationLoading={setTranslationLoading}
                    setTranslations={setTranslations}
                    translationLanguage={translationLanguage}
                    setTranslationLanguage={setTranslationLanguage}
                  />
                  <ToggleSlider
                    value={!showTranslation}
                    onChange={() => setShowTranslation(!showTranslation)}
                    disabled={translationLanguage == null || translations == null}
                  />
                  <ToggleLabel>Show original</ToggleLabel>
                </FeatureFlagged>
              )}
            </HeaderCenterItems>
            <DocumentViewerDownload files={displayFiles} documentToken={document?.id} />
          </>
        </DocumentViewerHeader>
        <DocumentContent>
          <DocumentViewerRendererContainer>
            {translationLoading && <LoadingLabel title='Translating...' />}
            <DocumentViewerRenderer
              files={displayFiles}
              rotation={rotation}
              nullStateMessage={nullStateMessage}
              nullStateRedirect={nullStateRedirect}
              showMessage={showMessage}
              annotations={fileAnnotationsOverlay}
              zoomScale={zoomScale}
              onFileLoad={setFileNumPages}
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              documentFileContainerRef={documentFileContainerRef}
            />
          </DocumentViewerRendererContainer>
          {extractions.length > 0 && (
            <ExtractionSidebar
              extractions={extractions}
              focusedExtractions={focusedExtractions}
              setFocusedExtractions={setFocusedExtraction}
            />
          )}
        </DocumentContent>
      </DocumentViewerWrapper>
    </ThemeProvider>
  );
};

const mapStateToProps = (state: RootState, ownProps: DocumentViewerBaseProps) => {
  return {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    featureFlags: state.featureFlags.featureFlags,
    extractions: state.documentViewer.documentGeneric.extractions,
    extractionFilters: state.documentViewer.documentGeneric.extractionFilters,
    focusedExtractions: state.documentViewer.documentGeneric.focusedExtractions,
    hoveredExtractions: state.documentViewer.documentGeneric.hoveredExtractions,
    inSearchMode: state.documentViewer.documentGeneric.inSearchMode,
    searchMatches: state.documentViewer.documentGeneric.searchMatches,
    searchIndex: state.documentViewer.documentGeneric.searchIndex,
    numPagesPerFile: state.documentViewer.documentGeneric.numPagesPerFile,
    translationLoading: state.documentViewer.documentGeneric.translationLoading,
    translations: state.documentViewer.documentGeneric.translations,
    translationLanguage: state.documentViewer.documentGeneric.translationLanguage,
    ...ownProps,
  };
};

const mapDispatchToProps = {
  showMessage,
  resetDocumentGenericState,
  setFocusedExtraction,
  removeFocusedExtraction,
  setHoveredExtraction,
  removeHoveredExtraction,
  setExtractionFilters,
  setExtractions,
  setInSearchMode,
  setSearchHighlights,
  setSearchIndex,
  setFileNumPages,
  setTranslationLoading,
  setTranslations,
  setTranslationLanguage,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DocumentGenericViewer);
